import * as React from 'react'
import { Check, Loader2 } from 'lucide-react'
import type {
  AcceptanceBlock,
  CoverBlock,
  FactsBlock,
  FutureWorkBlock,
  IrrigationFindingsBlock,
  IrrigationRepairRow,
  IrrigationScheduleBlock,
  JobLegendBlock,
  PhotosBlock,
  ProblemsBlock,
  ProseBlock,
  SinceLastVisitBlock,
  SiteMapBlock,
  SummaryBlock,
  TreeScheduleBlock,
  TreeScheduleRow,
} from '@lib/dto/proposal-document.dto'
import type { ProposalSelectionDto } from '@lib/dto/proposal-selection.dto'
import { JOB_TYPE_LABELS, type JobType } from '@lib/enums/job-type.enum'
import type { ProposalChapter } from '@lib/enums/proposal-chapter.enum'
import {
  PROBLEM_SEVERITY_DESCRIPTIONS,
  PROBLEM_SEVERITY_LABELS,
  type ProblemSeverity,
} from '@lib/enums/problem-severity.enum'
import { planLabel } from '@lib/enums/proposal-plan.enum'
import { UNIDENTIFIED_LABEL } from '@lib/enums/tree-mark.enum'
import { SEASON_LABELS } from '@lib/enums/season.enum'
import { formatMoneyAmount, parseMoney } from '@lib/helpers/money'
import {
  scheduleRowPriority,
  treePriority,
  type PlanFigures,
} from '@lib/helpers/proposal-plans'
import { JobBadge } from '@/field-map/JobBadge'
import { TreeGlyph } from '@/field-map/TreeGlyph'
import { useRequestMeeting } from '@/hooks/useProposalMeetings'
import { toErrorMessage } from '@/helpers/logger'
import { cn } from '@/lib/utils'
import { encodeVersion } from '@/schemas/proposalVersionLink'
import combinedBand from '@/proposal-document/bands/combined.svg'
import soilBand from '@/proposal-document/bands/soil.svg'
import treesBand from '@/proposal-document/bands/trees.svg'
import waterBand from '@/proposal-document/bands/water.svg'
import type { Choosing } from '@/proposal-document/choosing'
import { PRIORITY_LABELS, severityClass } from '@/proposal-document/canopy'
import { JOB_ANIMATIONS } from '@/proposal-document/jobAnimations'
import { ProposalPhoto, useCanShowPhotos } from '@/proposal-document/Photo'
import { Qr } from '@/proposal-document/Qr'
import { repairLabel } from '@/proposal-document/repairs'
import { SitePlanSvg } from '@/proposal-document/SitePlanSvg'
import { scheduleRowId } from '@/proposal-document/anchors'
import {
  formatLongDate,
  taxRateLabel,
  wholeDollars,
  workCount,
} from '@/proposal-document/PlanChapter'
import { splitQuestion, splitReview } from '@/proposal-document/text'
import { useTrack } from '@/proposal-document/activity'

/**
 * The chapters of the proposal, in the order a board asks its questions.
 *
 * These are a document, not app chrome. The voice is the marketing site's:
 * Archivo, Plex, the bands, square rules, gold for the one thing to press.
 * Classes live in `document.css`, colors in the `--doc-*` tokens.
 */

/** Whether the arborist marked work optional, splitting "everything" in two. */
export function hasOptionalWork(figures: PlanFigures[]): boolean {
  return figures.some((figure) => figure.plan === 'CORE' && figure.isAvailable)
}

/** The plan we recommend: CORE when there is optional work, else ALL. */
export function recommendedOf(figures: PlanFigures[]): 'CORE' | 'ALL' {
  return hasOptionalWork(figures) ? 'CORE' : 'ALL'
}

/** A chapter: an anchor for the chapter list, a kicker, a heading. */
export function Chapter({
  id,
  kicker,
  title,
  lede,
  wide = false,
  screenOnly = false,
  className,
  children,
}: {
  id: ProposalChapter
  kicker: string
  title: string
  lede?: React.ReactNode
  /** Evidence runs the full width of the column; text stays at reading length. */
  wide?: boolean
  /** The lede tells somebody what to tap, which means nothing on paper. */
  screenOnly?: boolean
  className?: string
  children: React.ReactNode
}) {
  return (
    <section
      id={id}
      className={cn('chapter', wide && 'chapter--wide', className)}
      aria-labelledby={`${id}-title`}
      data-chapter={id}
    >
      <header className="chapter-head">
        <p className="proposal-kicker">{kicker}</p>
        <h2 id={`${id}-title`} className="chapter-title">
          {title}
        </h2>
        {lede === undefined ? null : (
          <p className={cn('chapter-lede', screenOnly && 'chapter-lede--screen')}>
            {lede}
          </p>
        )}
      </header>
      {children}
    </section>
  )
}

const LETTERHEAD_BANDS = [
  { src: waterBand, label: 'Water' },
  { src: treesBand, label: 'Trees' },
  { src: soilBand, label: 'Soil' },
] as const

/** The water, trees and soil trio that opens every page of the site. */
export function BandTrio({ className }: { className?: string }) {
  return (
    <div className={cn('proposal-band', className)} aria-hidden="true">
      {LETTERHEAD_BANDS.map((band) => (
        <img key={band.label} src={band.src} alt="" />
      ))}
    </div>
  )
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

function siteList(names: string[]): string {
  if (names.length === 0) return 'the property'
  if (names.length === 1) return names[0] ?? 'the property'
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`
}

/** The person who walked the site, or the business when nobody is named. */
function preparer(cover: CoverBlock): string {
  return cover.preparedByName ?? cover.preparedByOrganization
}

/**
 * The opening: their property, who walked it, what it comes to.
 *
 * The deep-green split from the marketing site, so a link from an email lands
 * somewhere that looks like the business and not like its software. The
 * photo is the first one attached to the proposal; without one, the site
 * plan stands in, which is the picture no other bid can show.
 */
export function Hero({
  cover,
  summary,
  firstSite,
  firstSchedule,
  firstIrrigation = null,
  figures,
  amountDue,
}: {
  cover: CoverBlock
  summary: SummaryBlock | null
  firstSite: SiteMapBlock | null
  firstSchedule: TreeScheduleBlock | null
  /** That site's repairs, which number its tags. */
  firstIrrigation?: IrrigationScheduleBlock | null
  figures: PlanFigures[]
  amountDue: string | null
}) {
  const canShowPhotos = useCanShowPhotos()
  const hazards = figures.find((figure) => figure.plan === 'HAZARDS')
  // Each plan's figure is already taxed as billed: trees whole, repairs by item.
  const hazardsDue = hazards?.isAvailable === true ? hazards.amountDue : null
  const treeCount = summary?.treeCount ?? 0
  const repairCount = summary?.irrigationRepairCount ?? 0
  const recorded =
    repairCount === 0
      ? `${String(treeCount)} ${treeCount === 1 ? 'tree' : 'trees'}`
      : treeCount === 0
        ? `${String(repairCount)} irrigation ${repairCount === 1 ? 'repair' : 'repairs'}`
        : `${String(treeCount)} ${treeCount === 1 ? 'tree' : 'trees'} and ${String(repairCount)} irrigation ${repairCount === 1 ? 'repair' : 'repairs'}`
  const walked =
    cover.surveyedOn === null
      ? `${preparer(cover)} surveyed ${siteList(cover.sites.map((site) => site.name))}`
      : `${preparer(cover)} walked ${siteList(cover.sites.map((site) => site.name))} on ${formatShortDate(cover.surveyedOn)}`

  /** The cover photo when there is one; otherwise their site, drawn. */
  function heroMedia(): React.ReactNode {
    if (cover.coverPhotoDocumentId !== null && canShowPhotos) {
      return (
        <ProposalPhoto
          documentId={cover.coverPhotoDocumentId}
          alt={`${cover.clientName}, photographed during the survey`}
          eager
        />
      )
    }
    if (firstSite === null) return null
    return (
      <div className="hero-plan">
        <SitePlanSvg
          block={firstSite}
          rows={firstSchedule?.rows ?? []}
          irrigation={firstIrrigation ?? undefined}
          aspectMax={0.9}
        />
      </div>
    )
  }

  return (
    <header className="hero">
      <div className="hero-media">{heroMedia()}</div>
      <div className="hero-body">
        <p className="hero-kicker">
          Proposal · {formatLongDate(cover.issuedOn)}
        </p>
        <h1 className="hero-title">{cover.title}</h1>
        <p className="hero-lede">
          {walked} and recorded {recorded}, one by one. Here is what we found,
          and what we would do about it.
        </p>
        <dl className="hero-facts">
          <div>
            <dt>Prepared for</dt>
            <dd>
              {cover.preparedForName ?? cover.clientName}
              {cover.preparedForTitle === null ? null : `, ${cover.preparedForTitle}`}
              {cover.preparedForName === null ? null : (
                <span className="hero-fact-sub">{cover.clientName}</span>
              )}
            </dd>
          </div>
          {amountDue === null ? null : (
            <div>
              <dt>Recommended</dt>
              <dd>
                {formatMoneyAmount(amountDue)}
                {hazardsDue === null ? null : (
                  <span className="hero-fact-sub">
                    or {formatMoneyAmount(hazardsDue)} for the hazards only
                  </span>
                )}
              </dd>
            </div>
          )}
          {cover.validUntil === null ? null : (
            <div>
              <dt>Price holds</dt>
              <dd>Until {formatLongDate(cover.validUntil)}</dd>
            </div>
          )}
        </dl>
        <div className="hero-actions">
          <a href="#plan" className="proposal-btn proposal-btn--gold">
            See the plan
          </a>
          {cover.phone === null ? null : (
            <a
              href={`tel:${cover.phone.replace(/[^0-9+]/g, '')}`}
              className="proposal-btn proposal-btn--outline-light"
            >
              Call {cover.phone}
            </a>
          )}
        </div>
        {cover.preparedByCredential === null ? null : (
          <p className="hero-credential">
            {preparer(cover)} · {cover.preparedByCredential}
          </p>
        )}
      </div>
    </header>
  )
}

/**
 * The printed cover: a page of its own, light on ink.
 *
 * Deep green stays on the screen; on paper it is a rule. The plan of their
 * site is the picture, the combined band closes the page as it closes the
 * website, and a QR code takes a board member from the paper to the page
 * where they can change the plan and accept.
 */
export function PrintCover({
  cover,
  firstSite,
  firstSchedule,
  firstIrrigation = null,
  shareUrl,
}: {
  cover: CoverBlock
  firstSite: SiteMapBlock | null
  firstSchedule: TreeScheduleBlock | null
  firstIrrigation?: IrrigationScheduleBlock | null
  shareUrl: string | null
}) {
  return (
    <section className="print-cover" aria-hidden="true">
      <BandTrio className="print-cover-trio" />
      <div className="print-cover-head">
        {cover.logoUrl === null ? (
          <p className="proposal-wordmark">{cover.preparedByOrganization}</p>
        ) : (
          <img src={cover.logoUrl} alt="" className="proposal-logo" />
        )}
        <p className="proposal-tag">Proposal · {formatLongDate(cover.issuedOn)}</p>
      </div>
      <h1 className="print-cover-title">{cover.title}</h1>
      <p className="print-cover-client">{cover.clientName}</p>
      {firstSite === null ? null : (
        <div className="print-cover-plan">
          <SitePlanSvg
            block={firstSite}
            rows={firstSchedule?.rows ?? []}
            irrigation={firstIrrigation ?? undefined}
            aspectMax={0.62}
          />
        </div>
      )}
      <div className="print-cover-facts">
        <div>
          <p className="proposal-label">Prepared for</p>
          <p>
            {cover.preparedForName ?? cover.clientName}
            {cover.preparedForTitle === null ? '' : `, ${cover.preparedForTitle}`}
          </p>
        </div>
        <div>
          <p className="proposal-label">Prepared by</p>
          <p>
            {preparer(cover)}
            {cover.preparedByCredential === null ? '' : `, ${cover.preparedByCredential}`}
          </p>
        </div>
        {/* No date was set: say nothing rather than print a placeholder. */}
        {cover.validUntil === null ? null : (
          <div>
            <p className="proposal-label">Price holds</p>
            <p>Until {formatLongDate(cover.validUntil)}</p>
          </div>
        )}
        {shareUrl === null ? null : (
          <div className="print-cover-scan">
            <Qr value={shareUrl} className="print-qr" />
            <p className="proposal-small">Scan to see the photos and accept online.</p>
          </div>
        )}
      </div>
      <img src={combinedBand} alt="" className="print-cover-band" />
    </section>
  )
}

/** "The short version": the page a board reads if it reads one. */
export function ShortVersion({
  narrative,
  summary,
  figures,
  amountDue,
  hasChoosing,
}: {
  narrative: ProseBlock | null
  summary: SummaryBlock | null
  figures: PlanFigures[]
  amountDue: string | null
  hasChoosing: boolean
}) {
  const homeCount = summary?.homeCount ?? null
  const due = parseMoney(amountDue)
  const available = figures.filter((figure) => figure.isAvailable)
  // Irrigation alone has no trees to count, rate or walk through.
  const isIrrigationAlone =
    summary !== null && summary.treeCount === 0 && (summary.irrigationRepairCount ?? 0) > 0

  /** Price per home where the homes are known; acreage as the fallback. */
  function lastFigure(): React.ReactNode {
    if (due !== null && homeCount !== null) {
      return (
        <div>
          <dt>Per home</dt>
          <dd>{wholeDollars(due / homeCount)}</dd>
        </div>
      )
    }
    if (summary?.acreage == null) return null
    return (
      <div>
        <dt>Acres</dt>
        <dd>{summary.acreage}</dd>
      </div>
    )
  }

  return (
    <Chapter
      id="short"
      kicker="The short version"
      title={narrative?.title ?? 'Why this work, and why now'}
    >
      <div className="chapter-text">
        {(narrative?.paragraphs ?? []).map((paragraph, index) => (
          <p key={`p-${String(index)}`}>{paragraph}</p>
        ))}
      </div>
      {summary === null ? null : (
        <dl className="figures">
          {isIrrigationAlone ? null : (
            <div>
              <dt>Trees surveyed</dt>
              <dd>{summary.treeCount}</dd>
            </div>
          )}
          {(summary.irrigationRepairCount ?? 0) === 0 ? null : (
            <div>
              <dt>Irrigation repairs</dt>
              <dd>{summary.irrigationRepairCount}</dd>
            </div>
          )}
          {isIrrigationAlone || summary.hazardTreeCount === null ? null : (
            <div>
              <dt>Hazards</dt>
              <dd>{summary.hazardTreeCount}</dd>
            </div>
          )}
          {due === null ? null : (
            <div>
              <dt>Recommended</dt>
              <dd>{wholeDollars(due)}</dd>
            </div>
          )}
          {lastFigure()}
        </dl>
      )}
      {available.length < 2 ? null : (
        <ul className="plan-lines">
          {available.map((figure) => (
            <li key={figure.plan}>
              <span className="plan-lines-name">
                {planLabel(figure.plan, hasOptionalWork(figures))}
                {figure.plan === recommendedOf(figures) ? (
                  <span className="proposal-pill">Recommended</span>
                ) : null}
              </span>
              <span className="proposal-mono">
                {formatMoneyAmount(figure.amountDue)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {hasChoosing ? (
        <p className="chapter-next print:hidden">
          <a href="#plan" className="proposal-link">
            {figures.filter((figure) => figure.isAvailable).length > 1
              ? 'Choose a plan'
              : 'See the price'}
          </a>{' '}
          {isIrrigationAlone ? 'or read on: what we found.' : 'or read on: your property, tree by tree.'}
        </p>
      ) : null}
    </Chapter>
  )
}

/**
 * What a finding points at on the plan.
 *
 * A tree finding names trees. An irrigation finding names repairs. The two
 * do not travel together: an optional repair set made every tree highlight
 * look like it might also be about repairs.
 */
export type FindingTarget =
  | { kind: 'trees'; site: string; labels: ReadonlySet<string> }
  | { kind: 'repairs'; site: string; repairKeys: ReadonlySet<string> }

/**
 * What was found, worst first, each with the photo that proves it.
 *
 * Grouped by kind, as the document always was: "mistletoe, on 8 trees, here is
 * what we do" is an argument where 46 rows of findings is an inventory. The
 * two worst open the chapter large; the rest follow compactly.
 */
/**
 * How serious a finding is, in words: one level when every tree with it
 * shares it, and the range when they differ ("Moderate to Serious"). Never
 * "Up to Serious", which reads as a hedge and is plainly wrong when every
 * tree is Serious.
 */
export function severityWords(
  maps: SiteMapBlock[],
  entry: ProblemsBlock['entries'][number],
): string | null {
  const levels: ProblemSeverity[] = []
  for (const map of maps) {
    for (const tree of map.trees) {
      for (const problem of tree.problems) {
        if (problem.name === entry.name && problem.severity !== null) {
          levels.push(problem.severity)
        }
      }
    }
  }
  return severityRange(levels, entry.worstSeverity)
}

/** "Serious", or "Moderate to Serious"; the worst alone when nothing is recorded. */
function severityRange(
  levels: ProblemSeverity[],
  worst: ProblemSeverity | null,
): string | null {
  const sorted = [...levels].sort((a, b) => a - b)
  const low = sorted[0]
  const high = sorted[sorted.length - 1]
  if (low === undefined || high === undefined) {
    return worst === null ? null : PROBLEM_SEVERITY_LABELS[worst]
  }
  return low === high
    ? PROBLEM_SEVERITY_LABELS[high]
    : `${PROBLEM_SEVERITY_LABELS[low]} to ${PROBLEM_SEVERITY_LABELS[high]}`
}

export function Findings({
  block,
  maps,
  choosing,
  onShowOnPlan,
}: {
  block: ProblemsBlock
  maps: SiteMapBlock[]
  choosing: Choosing | null
  onShowOnPlan: (name: string, target: FindingTarget) => void
}) {
  const canShowPhotos = useCanShowPhotos()

  /** The tree's own photos of this problem, never a stock image. */
  function evidence(name: string): { id: string; caption: string }[] {
    const found: { id: string; caption: string }[] = []
    for (const map of maps) {
      for (const tree of map.trees) {
        for (const problem of tree.problems) {
          if (problem.name !== name) continue
          for (const id of problem.photoDocumentIds) {
            found.push({ id, caption: `Tree ${tree.label} · ${name}` })
          }
        }
      }
    }
    return found
  }

  function target(labels: string[]): FindingTarget | null {
    for (const map of maps) {
      const own = map.trees.filter((tree) => labels.includes(tree.label))
      if (own.length > 0) {
        return {
          kind: 'trees',
          site: map.siteName,
          labels: new Set(own.map((tree) => tree.label)),
        }
      }
    }
    return null
  }

  const declined = (name: string): boolean =>
    choosing?.selection.excludedProblems.includes(name) ?? false

  return (
    <div className="findings">
      {block.entries.map((entry, index) => {
        const photos = canShowPhotos ? evidence(entry.name) : []
        const photo =
          photos[0] ??
          (entry.photoDocumentId === null || !canShowPhotos
            ? undefined
            : { id: entry.photoDocumentId, caption: entry.name })
        const isLead = index < 2 && photo !== undefined
        const where = target(entry.treeLabels)
        return (
          <article
            key={entry.name}
            className={cn(
              'finding',
              isLead && 'finding--lead',
              declined(entry.name) && 'is-declined',
            )}
          >
            {photo === undefined ? null : (
              <figure className="finding-photo">
                <ProposalPhoto documentId={photo.id} alt={`Survey photo: ${photo.caption}`} />
                <figcaption className="proposal-chip">{photo.caption}</figcaption>
              </figure>
            )}
            <div className="finding-body">
              <p className="finding-meta">
                {entry.worstSeverity === null ? null : (
                  <span
                    className={cn(
                      'proposal-severity',
                      severityClass(entry.worstSeverity),
                    )}
                  >
                    {severityWords(maps, entry)}
                  </span>
                )}
                <span className="proposal-tag">
                  {entry.treeLabels.length === 1
                    ? `Tree ${entry.treeLabels[0] ?? ''}`
                    : `${String(entry.treeLabels.length)} trees`}
                </span>
                {declined(entry.name) ? (
                  <span className="proposal-tag">Left out</span>
                ) : null}
              </p>
              <h3 className="finding-title">{entry.name}</h3>
              {entry.description === null ? null : <p>{entry.description}</p>}
              {entry.worstSeverity !== null &&
              treePriority(entry.worstSeverity) === 'hazard' ? (
                <p className="proposal-muted">
                  {PROBLEM_SEVERITY_LABELS[entry.worstSeverity]}:{' '}
                  {PROBLEM_SEVERITY_DESCRIPTIONS[entry.worstSeverity].toLowerCase()}
                </p>
              ) : null}
              {entry.treatments.length === 0 ? null : (
                <ul className="finding-treatments">
                  {entry.treatments.map((treatment) => (
                    <li key={treatment.label}>
                      <span className="proposal-strong">{treatment.label}</span>
                      {treatment.treeCount < entry.treeLabels.length ? (
                        <span className="proposal-muted">
                          {' '}
                          · {treatment.treeCount} of {entry.treeLabels.length} trees
                        </span>
                      ) : null}
                      {treatment.description === null ? null : (
                        <span className="finding-treatment-detail">
                          {treatment.description}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {entry.notes.length === 0 ? null : (
                <ul className="finding-notes">
                  {entry.notes.map((note) => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              )}
              <p className="finding-trees">
                <span className="proposal-label">Trees</span>{' '}
                <span className="proposal-mono">{entry.treeLabels.join(', ')}</span>
                {where === null ? null : (
                  <button
                    type="button"
                    className="proposal-link print:hidden"
                    onClick={() => {
                      onShowOnPlan(entry.name, where)
                    }}
                  >
                    Show on the plan
                  </button>
                )}
              </p>
            </div>
          </article>
        )
      })}
    </div>
  )
}

/**
 * What was found in the irrigation, as the tree findings are: one entry per
 * kind of repair, worst first, with how many and where.
 *
 * "Where" is the zone, in the words the technician walked the property by,
 * or what the repair was recorded on when it is in no zone ("Timer A",
 * "Backflow"). The plan shows the ones that stand somewhere, as it shows a
 * tree problem's trees.
 */
export function IrrigationFindings({
  block,
  schedules,
  maps = [],
  choosing,
  onShowOnPlan,
}: {
  block: IrrigationFindingsBlock
  schedules: IrrigationScheduleBlock[]
  /** The plans, to point at the repairs pinned on them. */
  maps?: SiteMapBlock[]
  choosing: Choosing | null
  onShowOnPlan?: (name: string, target: FindingTarget) => void
}) {
  const canShowPhotos = useCanShowPhotos()

  function repairsOf(name: string): { site: string; repair: IrrigationRepairRow }[] {
    return schedules.flatMap((schedule) =>
      schedule.repairs
        .filter((repair) => repair.kind === name)
        .map((repair) => ({ site: schedule.siteName, repair })),
    )
  }

  function places(entry: IrrigationFindingsBlock['entries'][number]): string[] {
    const found: string[] = []
    for (const { site, repair } of repairsOf(entry.name)) {
      const place = repair.zone ?? repair.subject
      const named = schedules.length > 1 ? `${site} · ${place}` : place
      if (!found.includes(named)) found.push(named)
    }
    return found.length > 0 ? found : entry.zones
  }

  /** The first plan showing a repair of this kind, and every one of them on it. */
  function target(name: string): FindingTarget | null {
    for (const map of maps) {
      const keys = new Set(
        repairsOf(name)
          .filter(({ site }) => site === map.siteName)
          .map(({ repair }) => repair.key),
      )
      const pinned = (map.irrigation?.repairs ?? []).filter((pin) => keys.has(pin.key))
      if (pinned.length > 0) {
        return {
          kind: 'repairs',
          site: map.siteName,
          repairKeys: new Set(pinned.map((pin) => pin.key)),
        }
      }
    }
    return null
  }

  /** Left out when every repair of the kind is, however it was declined. */
  function declined(name: string): boolean {
    if (choosing === null) return false
    const repairs = repairsOf(name)
    return (
      repairs.length > 0 &&
      repairs.every(({ site, repair }) => choosing.repairStatus(site, repair) === 'out')
    )
  }

  return (
    <div className="findings">
      {block.entries.map((entry, index) => {
        const photo =
          entry.photoDocumentId === null || !canShowPhotos ? null : entry.photoDocumentId
        const levels = repairsOf(entry.name).flatMap(({ repair }) =>
          repair.severity === null ? [] : [repair.severity],
        )
        const where = places(entry)
        const onPlan = onShowOnPlan === undefined ? null : target(entry.name)
        return (
          <article
            key={entry.name}
            className={cn(
              'finding',
              index < 2 && photo !== null && 'finding--lead',
              declined(entry.name) && 'is-declined',
            )}
          >
            {photo === null ? null : (
              <figure className="finding-photo">
                <ProposalPhoto documentId={photo} alt={`Survey photo: ${entry.name}`} />
                <figcaption className="proposal-chip">{entry.name}</figcaption>
              </figure>
            )}
            <div className="finding-body">
              <p className="finding-meta">
                {entry.worstSeverity === null ? null : (
                  <span
                    className={cn(
                      'proposal-severity',
                      severityClass(entry.worstSeverity),
                    )}
                  >
                    {severityRange(levels, entry.worstSeverity)}
                  </span>
                )}
                <span className="proposal-tag">{entry.count} found</span>
                {declined(entry.name) ? <span className="proposal-tag">Left out</span> : null}
              </p>
              <h3 className="finding-title">{entry.name}</h3>
              {entry.description === null ? null : <p>{entry.description}</p>}
              {entry.worstSeverity !== null &&
              treePriority(entry.worstSeverity) === 'hazard' ? (
                <p className="proposal-muted">
                  {PROBLEM_SEVERITY_LABELS[entry.worstSeverity]}:{' '}
                  {PROBLEM_SEVERITY_DESCRIPTIONS[entry.worstSeverity].toLowerCase()}
                </p>
              ) : null}
              {where.length === 0 ? null : (
                <p className="finding-trees">
                  <span className="proposal-label">Where</span>{' '}
                  <span className="proposal-mono">{where.join(', ')}</span>
                  {onPlan === null ? null : (
                    <button
                      type="button"
                      className="proposal-link print:hidden"
                      onClick={() => {
                        onShowOnPlan?.(entry.name, onPlan)
                      }}
                    >
                      Show on the plan
                    </button>
                  )}
                </p>
              )}
            </div>
          </article>
        )
      })}
    </div>
  )
}

/** Photos attached to the proposal by hand, after the cover: "From the field". */
/** "October 2026": a visit, as a client places it. */
export function monthOf(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  })
}

/** "#1", "#1 and #5", "#1, #5 and #9". */
export function andList(items: readonly string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1) ?? ''}`
}

/**
 * "Since our last visit": the problems this visit found again, and what has
 * changed on the property since the visit before (docs/site-visits.md,
 * "Proposals"). One for each site visited before with something to say,
 * named when there are several.
 */
export function SinceLastVisit({
  blocks,
  showSiteName,
}: {
  blocks: SinceLastVisitBlock[]
  showSiteName: boolean
}) {
  return (
    <div className="since-last-visit">
      {blocks.map((block) => (
        <section key={block.siteName} className="since-visit">
          {block.title === null ? null : (
            <h3 className="proposal-h3">
              {block.title}
              {showSiteName ? ` · ${block.siteName}` : ''}
            </h3>
          )}
          <p className="proposal-label">Last visit · {monthOf(block.since)}</p>
          {block.paragraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          {block.cameBack.length === 0 ? null : (
            <>
              <p className="proposal-label">Found again</p>
              <ul className="proposal-rule-list">
                {block.cameBack.map((entry) => (
                  <li key={entry.name}>
                    {entry.name}{' '}
                    {entry.where.every((where) => where.startsWith('#'))
                      ? 'on'
                      : 'at'}{' '}
                    {andList(entry.where)}, also found in{' '}
                    {monthOf(entry.foundOn)}.
                  </li>
                ))}
              </ul>
            </>
          )}
          {block.changes.length === 0 ? null : (
            <>
              <p className="proposal-label">What has changed</p>
              <ul className="proposal-rule-list">
                {block.changes.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </>
          )}
        </section>
      ))}
    </div>
  )
}

/** Whether a paragraph of wording is the footnote the asterisks point to. */
function isFootnote(paragraph: string): boolean {
  return paragraph.trimStart().startsWith('*')
}

/** An estimate as Future work prints it: always with its asterisk. */
function estimateWords(amount: string | null): string {
  return amount === null ? 'Not estimated' : `${formatMoneyAmount(amount)}*`
}

/**
 * Work recommended for a later season (docs/seasonal-work.md): by season,
 * then site, then tree, each priced as an estimate with an asterisk. The
 * wording's paragraphs open the chapter, and one starting with "*" closes it
 * as the footnote the asterisks point to.
 *
 * Nothing here can be chosen, so nothing here is a control: the client
 * approves the next visit, and this is rebid when its season comes.
 */
export function FutureWork({ block }: { block: FutureWorkBlock }) {
  const showSites =
    new Set(
      block.seasons.flatMap((season) =>
        season.sites.map((site) => site.siteName),
      ),
    ).size > 1
  return (
    <div className="future-work">
      {block.paragraphs
        .filter((paragraph) => !isFootnote(paragraph))
        .map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      {block.seasons.map((season) => (
        <section key={season.season} className="future-season">
          <h3 className="proposal-h3 future-season-head">
            <span>{SEASON_LABELS[season.season]}</span>
            <span className="proposal-mono">{estimateWords(season.amount)}</span>
          </h3>
          {season.sites.map((site) => (
            <div key={site.siteName} className="future-site">
              {showSites ? (
                <p className="proposal-label">
                  {site.siteName} · {estimateWords(site.amount)}
                </p>
              ) : null}
              <div className="proposal-scroll">
                <table className="proposal-table proposal-table--schedule">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Species</th>
                      <th>Work</th>
                      <th className="future-estimate">Estimate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {site.trees.map((tree) => (
                      <tr key={tree.label}>
                        <td className="proposal-mono">{tree.label}</td>
                        <td>
                          <span className="appendix-species">
                            <TreeGlyph species={tree.species} size={12} />
                            {tree.species?.name ?? UNIDENTIFIED_LABEL}
                          </span>
                        </td>
                        <td>
                          <span className="appendix-jobs">
                            {(tree.pruning?.jobs ?? []).map((job) => (
                              <span key={`${job.type}-${String(job.level)}`}>
                                <JobBadge type={job.type} level={job.level} height={14} />
                                {JOB_TYPE_LABELS[job.type]}
                              </span>
                            ))}
                            {tree.treatments.map((treatment) => (
                              <span key={`${treatment.problemName}-${treatment.label}`}>
                                {treatment.label}
                                <span className="proposal-muted">
                                  {' '}
                                  for {treatment.problemName.toLowerCase()}
                                </span>
                              </span>
                            ))}
                            {/* Indexed: two "Other" problems share a name. */}
                            {tree.findings.map((finding, index) => (
                              <span key={`finding-${String(index)}`}>{finding}</span>
                            ))}
                            {tree.removal === null ? null : (
                              <span className="proposal-muted">
                                {tree.pruning === null
                                  ? 'Removal'
                                  : `Or removal instead, ${estimateWords(tree.removal.amount)}`}
                              </span>
                            )}
                            {tree.isOptional ? (
                              <span className="proposal-muted">Optional</span>
                            ) : null}
                          </span>
                        </td>
                        <td className="proposal-mono future-estimate">
                          {estimateWords(tree.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </section>
      ))}
      {block.paragraphs.filter(isFootnote).map((paragraph) => (
        <p key={paragraph} className="proposal-small future-footnote">
          {paragraph}
        </p>
      ))}
    </div>
  )
}

export function FieldPhotos({
  block,
  coverId,
}: {
  block: PhotosBlock
  coverId: string | null
}) {
  const canShowPhotos = useCanShowPhotos()
  const items = block.items.filter((item) => item.documentId !== coverId)
  if (!canShowPhotos || items.length === 0) return null
  return (
    <div className="field-photos">
      <p className="proposal-label">From the field</p>
      <ul>
        {items.map((item) => (
          <li key={item.documentId}>
            <figure>
              <ProposalPhoto documentId={item.documentId} alt={item.caption ?? 'A photo from the survey'} />
              {item.caption === null ? null : (
                <figcaption className="proposal-chip">{item.caption}</figcaption>
              )}
            </figure>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Why this bid: who did the survey, what others say, what is carried, and the
 * questions to put to every bidder. Set before the price, because that is
 * where a board starts comparing.
 */
export function Trust({
  cover,
  facts,
  reviews,
  comparison,
}: {
  cover: CoverBlock
  facts: FactsBlock | null
  reviews: ProseBlock | null
  comparison: ProseBlock | null
}) {
  return (
    <div className="trust">
      <div className="trust-person">
        <p className="proposal-label">Who walked your property</p>
        <p className="trust-name">{preparer(cover)}</p>
        {cover.preparedByCredential === null ? null : (
          <p className="proposal-tag">{cover.preparedByCredential}</p>
        )}
        <p>
          {cover.surveyedOn === null
            ? 'Recorded every tree in this proposal on site.'
            : `Recorded every tree in this proposal on site, last on ${formatLongDate(cover.surveyedOn)}.`}
        </p>
        {cover.phone === null ? null : (
          <p className="proposal-small">
            Questions? Call{' '}
            <a className="proposal-mono" href={`tel:${cover.phone.replace(/[^0-9+]/g, '')}`}>
              {cover.phone}
            </a>
            .
          </p>
        )}
      </div>

      {reviews === null || reviews.bullets.length === 0 ? null : (
        <div className="trust-reviews">
          <p className="proposal-label">{reviews.title ?? 'What customers say'}</p>
          {reviews.bullets.map((bullet) => {
            const { quote, attribution } = splitReview(bullet)
            return (
              <figure key={bullet} className="review">
                <span className="review-mark" aria-hidden>
                  “
                </span>
                <blockquote>{quote}</blockquote>
                {attribution === null ? null : <figcaption>{attribution}</figcaption>}
              </figure>
            )
          })}
        </div>
      )}

      {comparison === null || comparison.bullets.length === 0 ? null : (
        <div className="trust-compare">
          <p className="proposal-label">{comparison.title ?? 'Comparing bids?'}</p>
          {comparison.paragraphs.map((paragraph) => (
            <p key={paragraph} className="proposal-small">
              {paragraph}
            </p>
          ))}
          <ul>
            {comparison.bullets.map((bullet) => {
              const { question, answer } = splitQuestion(bullet)
              return (
                <li key={bullet}>
                  <span className="proposal-strong">{question}</span>
                  {answer === null ? null : <span className="proposal-muted">{answer}</span>}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {facts === null ? null : (
        <div className="trust-facts">
          <p className="proposal-label">{facts.title}</p>
          <dl className="proposal-facts">
            {facts.items.map((item) => (
              <div key={item.label} className="proposal-facts-row">
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  )
}

/**
 * How the work goes, as answers a reader opens one at a time.
 *
 * The wording is specific and good, and on screen it is the reader's to open:
 * ten bullets on safety are reassuring to the one board member who asks and a
 * wall to everybody else. Printed, every answer is open.
 */
export function HowItGoes({ sections }: { sections: ProseBlock[] }) {
  const [open, setOpen] = React.useState<string | null>(null)
  return (
    <div className="answers">
      {sections.map((section, index) => {
        const key = section.title ?? String(index)
        const isOpen = open === key
        const bodyId = `answer-${String(index)}`
        return (
          <div key={key} className={cn('answer', isOpen && 'is-open')}>
            <h3 className="answer-head">
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={bodyId}
                onClick={() => {
                  setOpen(isOpen ? null : key)
                }}
              >
                <span>{section.title}</span>
                <span className="answer-sign" aria-hidden>
                  {isOpen ? '−' : '+'}
                </span>
              </button>
            </h3>
            <div id={bodyId} className="answer-body">
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.bullets.length === 0 ? null : (
                <ul className="proposal-rule-list">
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** Everything a client needs to say yes from the shared page. */
export interface AcceptControls {
  token: string
  accepted: ProposalSelectionDto | null
  isPending: boolean
  error: string | null
  onAccept: () => void
}

/**
 * What happens next, and the yes itself.
 *
 * On screen a client accepts here, or asks for somebody at their board
 * meeting, or sends their version of the plan to a colleague. On paper the
 * same place carries signature lines and tick boxes for each plan, so a board
 * that votes on paper can still choose.
 */
export function NextSteps({
  steps,
  acceptance,
  choosing,
  accept,
  taxRate,
  figures,
  shareUrl,
  phone,
}: {
  steps: ProseBlock | null
  acceptance: AcceptanceBlock | null
  choosing: Choosing | null
  accept: AcceptControls | null
  taxRate: string | null
  figures: PlanFigures[]
  shareUrl: string | null
  phone: string | null
}) {
  return (
    <div className="next">
      {steps === null || steps.bullets.length === 0 ? null : (
        <ol className="steps">
          {steps.bullets.map((bullet, index) => (
            <li key={bullet}>
              <span className="steps-num" aria-hidden>
                {index + 1}
              </span>
              <span>{bullet}</span>
            </li>
          ))}
        </ol>
      )}

      {accept === null || choosing === null ? null : (
        <div className="next-actions print:hidden">
          <AcceptBox accept={accept} choosing={choosing} taxRate={taxRate} acceptance={acceptance} phone={phone} />
          <MeetingBox token={accept.token} note={acceptance?.note ?? null} />
        </div>
      )}

      {acceptance === null ? null : (
        <PrintSignature
          acceptance={acceptance}
          figures={figures}
          shareUrl={shareUrl}
        />
      )}
    </div>
  )
}

/** What an acceptance left aside, said the way its plan means it. */
function deferredWords(accepted: ProposalSelectionDto): string {
  if (accepted.deferredAmount === null) return ''
  const amount = formatMoneyAmount(accepted.deferredAmount)
  return accepted.plan === 'PHASED'
    ? `, with ${amount} planned for next year`
    : `, leaving ${amount} for later`
}

function AcceptBox({
  accept,
  choosing,
  taxRate,
  acceptance,
  phone,
}: {
  accept: AcceptControls
  choosing: Choosing
  taxRate: string | null
  acceptance: AcceptanceBlock | null
  phone: string | null
}) {
  const [copied, setCopied] = React.useState<string | null>(null)
  const track = useTrack()
  const outcome = choosing.outcome
  const rate = taxRateLabel(taxRate)

  if (accept.accepted !== null) {
    const accepted = accept.accepted
    return (
      <div id="accept" className="accept-box is-accepted">
        <p className="accept-done">
          <Check className="h-4 w-4" aria-hidden />
          Accepted. Thank you.
        </p>
        <p>
          You accepted {formatMoneyAmount(accepted.acceptedAmount)}
          {accepted.plan === null
            ? ''
            : ` (${planLabel(accepted.plan, choosing.hasOptional)})`}
          {deferredWords(accepted)}
          . Your answer is recorded against this proposal, and we will be in
          touch to agree the dates.
        </p>
      </div>
    )
  }

  async function copyLink(): Promise<void> {
    const url = `${window.location.origin}${window.location.pathname}#${encodeVersion(choosing.state)}`
    try {
      await navigator.clipboard.writeText(url)
      track({ kind: 'SHARED' })
      setCopied('Link copied. It opens this proposal with your plan and changes already chosen.')
    } catch (caught) {
      setCopied(`Copy this link: ${url} (${toErrorMessage(caught)})`)
    }
  }

  return (
    <div id="accept" className="accept-box">
      <p className="proposal-label">You are accepting</p>
      <p className="accept-plan">
        {planLabel(choosing.state.plan, choosing.hasOptional)}
        {choosing.changes.length === 0
          ? ''
          : `, with ${String(choosing.changes.length)} ${choosing.changes.length === 1 ? 'change' : 'changes'}`}
      </p>
      <p className="accept-amount">{formatMoneyAmount(outcome.amountDue)}</p>
      <p className="proposal-small">
        {outcome.taxAmount === null || rate === null
          ? 'No sales tax applies.'
          : `Includes ${formatMoneyAmount(outcome.taxAmount)} sales tax (${rate}).`}
        {acceptance?.validUntil == null ? '' : ` Price holds until ${formatLongDate(acceptance.validUntil)}.`}
      </p>
      {acceptance?.requiresBoardApproval === true ? (
        <p className="proposal-small">
          Where the association’s governing documents require a board vote,
          accept once whoever holds that authority has agreed.
        </p>
      ) : null}
      {accept.error === null ? null : (
        <p className="proposal-note proposal-danger">{accept.error}</p>
      )}
      <div className="accept-buttons">
        <button
          type="button"
          className="proposal-btn proposal-btn--gold"
          disabled={accept.isPending}
          onClick={accept.onAccept}
        >
          {accept.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          Accept · {formatMoneyAmount(outcome.amountDue)}
        </button>
        <button
          type="button"
          className="proposal-btn proposal-btn--outline proposal-btn--sm"
          onClick={() => {
            void copyLink()
          }}
        >
          Share this version
        </button>
      </div>
      {copied === null ? null : (
        <p className="proposal-small" role="status">
          {copied}
        </p>
      )}
      {phone === null ? null : (
        <p className="proposal-small">
          Rather talk it through? Call{' '}
          <a className="proposal-mono" href={`tel:${phone.replace(/[^0-9+]/g, '')}`}>
            {phone}
          </a>
          .
        </p>
      )}
    </div>
  )
}

/** "Want us at the board meeting?" — the request that gets us in the room. */
function MeetingBox({ token, note }: { token: string; note: string | null }) {
  const request = useRequestMeeting(token)
  const [date, setDate] = React.useState('')
  const [message, setMessage] = React.useState('')

  return (
    <div className="meeting-box">
      <p className="proposal-label">Board meeting</p>
      <p className="accept-plan">Want us at the meeting?</p>
      <p className="proposal-small">
        {note ??
          'We will come and answer the board’s questions in person, at no charge, whether or not the work is awarded.'}
      </p>
      {request.isSuccess ? (
        <p className="proposal-small" role="status">
          Asked for {formatLongDate(`${request.data.meetingDate}T00:00:00.000Z`)}.
          We will confirm by phone or email.
        </p>
      ) : (
        <form
          className="meeting-form"
          onSubmit={(event) => {
            event.preventDefault()
            if (date === '') return
            request.mutate({ meetingDate: date, note: message })
          }}
        >
          <label htmlFor="meeting-date" className="proposal-label">
            Meeting date
          </label>
          <input
            id="meeting-date"
            type="date"
            required
            value={date}
            onChange={(event) => {
              setDate(event.target.value)
            }}
          />
          <label htmlFor="meeting-note" className="proposal-label">
            Time and place, if you know them
          </label>
          <input
            id="meeting-note"
            type="text"
            maxLength={1000}
            value={message}
            onChange={(event) => {
              setMessage(event.target.value)
            }}
          />
          {request.isError ? (
            <p className="proposal-note proposal-danger">
              {toErrorMessage(request.error)}
            </p>
          ) : null}
          <button
            type="submit"
            className="proposal-btn proposal-btn--outline proposal-btn--sm"
            disabled={request.isPending || date === ''}
          >
            Ask us to attend
          </button>
        </form>
      )}
    </div>
  )
}

/** On paper: tick a plan, sign, date. */
function PrintSignature({
  acceptance,
  figures,
  shareUrl,
}: {
  acceptance: AcceptanceBlock
  figures: PlanFigures[]
  shareUrl: string | null
}) {
  const available = figures.filter((figure) => figure.isAvailable)
  // The whole proposal as billed, from the same arithmetic as the page.
  const offered =
    figures.find((figure) => figure.plan === 'ALL')?.amountDue ??
    acceptance.totalAmount
  return (
    <div className="print-sign">
      {/* Often the top of its own page, so it says what it is. */}
      <p className="proposal-label print-sign-label">
        {available.length < 2 ? 'To accept, sign below' : 'To accept, tick a plan and sign below'}
      </p>
      {available.length < 2 ? (
        <p className="print-sign-total">
          <span className="proposal-label">Total</span>{' '}
          <span className="proposal-mono">{formatMoneyAmount(offered)}</span>
        </p>
      ) : (
        <ul className="print-sign-plans">
          {available.map((figure) => (
            <li key={figure.plan}>
              <span className="print-box" aria-hidden />
              <span className="proposal-strong">
                {planLabel(figure.plan, hasOptionalWork(figures))}
              </span>
              <span className="proposal-muted">
                {' '}
                · {workCount(figure.treeCount, figure.repairCount)}
              </span>
              <span className="proposal-mono">
                {formatMoneyAmount(figure.amountDue)}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="proposal-signs">
        <div className="proposal-sign">
          <div className="proposal-sign-line" />
          <p>{acceptance.signerLabel}</p>
        </div>
        <div className="proposal-sign">
          <div className="proposal-sign-line" />
          <p>Date</p>
        </div>
      </div>
      <div className="proposal-signs">
        <div className="proposal-sign">
          <div className="proposal-sign-line" />
          <p>Printed name and title</p>
        </div>
      </div>
      {shareUrl === null ? null : (
        <div className="print-sign-qr">
          <Qr value={shareUrl} className="print-qr" />
          <p className="proposal-small">
            Prefer to accept online? Scan the code to open this proposal, choose
            a plan and accept.
          </p>
        </div>
      )}
    </div>
  )
}

/** How many rows of a schedule show before "Show all". */
const APPENDIX_ROWS = 10

/** A kind of work, drawn being done, where it has a drawing. */
function JobAnimation({ type }: { type: JobType }) {
  const markup = JOB_ANIMATIONS[type]
  if (markup === null) return null
  return <dd className="appendix-legend-art" dangerouslySetInnerHTML={{ __html: markup }} />
}

/**
 * The reference matter: the full schedules, what the work means, exclusions,
 * terms.
 *
 * The schedules are the bid as offered, not as chosen: what a client leaves
 * out already shows in the plan and the price, so the appendix does not cross
 * it out again. A long schedule shows its first rows and the rest on request,
 * since a property can have hundreds of trees; printed, it is whole.
 */
export function Appendix({
  schedules,
  irrigationSchedules,
  legend,
  exclusions,
  terms,
  markUnestimated,
}: {
  schedules: TreeScheduleBlock[]
  irrigationSchedules: IrrigationScheduleBlock[]
  legend: JobLegendBlock | null
  exclusions: ProseBlock | null
  terms: ProseBlock | null
  markUnestimated: boolean
}) {
  const [shown, setShown] = React.useState<ReadonlySet<string>>(() => new Set())
  const isShown = (key: string): boolean => shown.has(key)
  const toggle = (key: string): void => {
    setShown((current) => {
      const next = new Set(current)
      if (!next.delete(key)) next.add(key)
      return next
    })
  }
  const rowClass = (key: string, index: number): string | undefined =>
    index >= APPENDIX_ROWS && !isShown(key) ? 'appendix-row--more' : undefined

  return (
    <div className="appendix">
      {schedules.map((schedule) => {
        const key = `trees:${schedule.siteName}`
        return (
          <div key={key} className="appendix-block">
            <h3 className="proposal-h3">
              Tree schedule{schedules.length > 1 ? ` · ${schedule.siteName}` : ''}
              {schedule.season === null || schedule.season === undefined
                ? ''
                : ` · ${SEASON_LABELS[schedule.season]} visit`}
            </h3>
            <div className="proposal-scroll">
              <table className="proposal-table proposal-table--schedule">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Species</th>
                    <th>Work</th>
                    <th>Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.rows.map((row, index) => (
                    <tr
                      key={row.label}
                      id={`${scheduleRowId(schedule.siteName, row.label)}-appendix`}
                      className={rowClass(key, index)}
                    >
                      <td className="proposal-mono">
                        {row.label}
                        {markUnestimated && row.estimatedLaborMinutes === null ? (
                          <span className="proposal-warn print:hidden"> · no estimate</span>
                        ) : null}
                      </td>
                      <td>
                        <span className="appendix-species">
                          <TreeGlyph species={row.species} size={12} />
                          {row.species?.name ?? UNIDENTIFIED_LABEL}
                        </span>
                      </td>
                      <td>
                        <span className="appendix-jobs">
                          {row.jobs.map((job) => (
                            <span key={`${job.type}-${String(job.level)}`}>
                              <JobBadge type={job.type} level={job.level} height={14} />
                              {JOB_TYPE_LABELS[job.type]}
                            </span>
                          ))}
                          {row.noPruningRecommended === true ? (
                            <span className="proposal-muted">No pruning recommended</span>
                          ) : null}
                          {row.removal === null ? null : (
                            <span className="proposal-muted">Removal quoted</span>
                          )}
                        </span>
                      </td>
                      <td>{priorityWord(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ShowAll
              count={schedule.rows.length}
              noun="trees"
              isShown={isShown(key)}
              onToggle={() => {
                toggle(key)
              }}
            />
          </div>
        )
      })}

      {irrigationSchedules.map((schedule) => {
        const key = `repairs:${schedule.siteName}`
        return (
          <div key={key} className="appendix-block">
            <h3 className="proposal-h3">
              Irrigation repairs
              {irrigationSchedules.length > 1 ? ` · ${schedule.siteName}` : ''}
            </h3>
            <div className="proposal-scroll">
              <table className="proposal-table proposal-table--schedule">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Repair</th>
                    <th>Where</th>
                    <th>How many</th>
                    <th>Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.repairs.map((repair, index) => (
                    <tr key={repair.key} className={rowClass(key, index)}>
                      <td className="proposal-mono">{repairLabel(index)}</td>
                      <td>{repair.kind}</td>
                      <td>{repair.subject}</td>
                      <td className="proposal-mono">{repair.count}</td>
                      <td>
                        {repair.severity === null ? '—' : PROBLEM_SEVERITY_LABELS[repair.severity]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ShowAll
              count={schedule.repairs.length}
              noun="repairs"
              isShown={isShown(key)}
              onToggle={() => {
                toggle(key)
              }}
            />
            {rateWords(schedule) === null ? null : (
              <p className="proposal-muted">{rateWords(schedule)}</p>
            )}
          </div>
        )
      })}

      {legend === null ? null : (
        <div className="appendix-block">
          <h3 className="proposal-h3">What the work means</h3>
          <dl className="appendix-legend">
            {legend.entries.map((entry) => (
              <div key={entry.jobType}>
                <dt>
                  <JobBadge type={entry.jobType} level={1} height={20} />
                  {entry.label}
                </dt>
                <JobAnimation type={entry.jobType} />
                <dd>{entry.description}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {[exclusions, terms].map((section) =>
        section === null ? null : (
          <div key={section.title ?? 'section'} className="appendix-block">
            <h3 className="proposal-h3">{section.title}</h3>
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            {section.bullets.length === 0 ? null : (
              <ul className="proposal-rule-list">
                {section.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            )}
          </div>
        ),
      )}
    </div>
  )
}

/** "Show all 225 trees" under a long schedule, and back again. */
function ShowAll({
  count,
  noun,
  isShown,
  onToggle,
}: {
  count: number
  noun: string
  isShown: boolean
  onToggle: () => void
}) {
  if (count <= APPENDIX_ROWS) return null
  return (
    <button
      type="button"
      className="proposal-link appendix-toggle print:hidden"
      aria-expanded={isShown}
      onClick={onToggle}
    >
      {isShown
        ? `Show the first ${String(APPENDIX_ROWS)}`
        : `Show all ${String(count)} ${noun}`}
    </button>
  )
}

/** "an hour", "30 minutes": the least a visit's labor is billed. */
function minimumWords(minutes: number): string {
  if (minutes === 60) return 'an hour'
  if (minutes % 60 === 0) return `${String(minutes / 60)} hours`
  return `${String(minutes)} minutes`
}

/** "by the quarter hour", "in 10-minute steps": how the rest is rounded. */
function incrementWords(minutes: number): string {
  if (minutes === 15) return 'by the quarter hour'
  if (minutes === 30) return 'by the half hour'
  if (minutes === 60) return 'by the hour'
  return `in ${String(minutes)}-minute steps`
}

/** The labor rate, and how a visit's labor is billed when the rule was frozen. */
function laborWords(
  labor: NonNullable<IrrigationScheduleBlock['rateCard']['labor']>,
): string {
  const rate = formatMoneyAmount(labor.pricePerHour)
  if (labor.minimumMinutes === 0 && labor.incrementMinutes <= 1) {
    return `Labor at ${rate} an hour, parts at list price.`
  }
  return `Labor at ${rate} an hour, billed ${minimumWords(labor.minimumMinutes)} at least and then ${incrementWords(labor.incrementMinutes)}. Parts at list price.`
}

/**
 * What the repairs are priced by, under their table: the labor rate and how
 * it is billed, and the trip, as Housecall Pro had them when the proposal was
 * built. A proposal from before the billing rule bills exact minutes and says
 * only the rate, as it did.
 */
function rateWords(schedule: IrrigationScheduleBlock): string | null {
  const { labor, driveCharge } = schedule.rateCard
  const parts = [
    labor === null ? null : laborWords(labor),
    driveCharge === null
      ? null
      : `One ${formatMoneyAmount(driveCharge.price)} charge to drive to ${driveCharge.town}.`,
  ]
  const words = parts.filter((part) => part !== null).join(' ')
  return words === '' ? null : words
}

function priorityWord(row: TreeScheduleRow): string {
  return PRIORITY_LABELS[scheduleRowPriority(row)]
}

/** The site's own closing: the combined band, then the ink bar with the phone. */
export function Closing({
  organization,
  phone,
}: {
  organization: string
  phone: string | null
}) {
  return (
    <footer className="closing">
      <img src={combinedBand} alt="" aria-hidden="true" className="closing-band" />
      <div className="closing-bar">
        <p className="closing-title">Questions? We are glad to talk it through.</p>
        <p className="closing-phone">
          {phone === null ? organization : (
            <a href={`tel:${phone.replace(/[^0-9+]/g, '')}`}>Call {phone}</a>
          )}
        </p>
      </div>
    </footer>
  )
}
